/**
 * Apply a centered moving average to smooth data (like DataDog).
 * Adapts window size based on data length for consistent visual smoothing.
 */
export function smoothData(data: number[], windowSize?: number): number[] {
  if (data.length < 3) return data;

  // Scale window size with data length for consistent visual smoothing
  // Fewer points = larger window percentage to maintain smoothness
  let window: number;
  if (windowSize !== undefined) {
    window = windowSize;
  } else if (data.length < 20) {
    // Very zoomed in: use 25-30% of data points
    window = Math.max(3, Math.floor(data.length * 0.3));
  } else if (data.length < 50) {
    // Moderate zoom: use ~15% of data points
    window = Math.max(5, Math.floor(data.length * 0.15));
  } else {
    // Full view: use ~8% of data points, max 21
    window = Math.max(5, Math.min(21, Math.floor(data.length * 0.08)));
  }

  const halfWindow = Math.floor(window / 2);

  return data.map((_, i) => {
    const start = Math.max(0, i - halfWindow);
    const end = Math.min(data.length, i + halfWindow + 1);
    const slice = data.slice(start, end);
    return slice.reduce((a, b) => a + b, 0) / slice.length;
  });
}

/**
 * Returns a formatted label for a chart based on the date and time range
 * - ≤24 hours: Time only "14:00"
 * - ≤2 days: Date & Time "Jan 5, 14:00"
 * - >2 days: Date only "Jan 5"
 */
export function formatChartLabel(date: Date, timeRangeMs: number): string {
  const hours = timeRangeMs / (1000 * 60 * 60);
  const days = hours / 24;
  if (hours <= 24) {
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  } else if (days <= 2) {
    return date.toLocaleDateString([], {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } else {
    return date.toLocaleDateString([], { month: "short", day: "numeric" });
  }
}

export function formatChartZoomRangeLabel(from: Date, to: Date): string {
  const fmt = (date: Date) =>
    date.toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  return `${fmt(from)} – ${fmt(to)}`;
}

/**
 * Convert a telemetry `bucket_time_unix_nano` string (nanoseconds since epoch,
 * too large for a JS number) into a `Date`.
 */
export function unixNanoToDate(value: string): Date {
  const nanos = BigInt(value);
  const millis = Number(nanos / 1_000_000n);
  return new Date(millis);
}

/**
 * The same conversion as {@link unixNanoToDate}, as epoch milliseconds — for
 * bucket math. Unlike the Date variant it swallows malformed input (returns
 * 0) so a bad timestamp degrades a chart axis instead of crashing the page.
 */
export function unixNanoToMs(nano: string): number {
  try {
    return Number(BigInt(nano) / 1_000_000n);
  } catch {
    return 0;
  }
}

export type ThresholdConfig = {
  red: number;
  amber: number;
  inverted?: boolean; // Set to true if lower is better (like latency)
};
